import { message } from 'antd'
import queries, { useGetErrorMessage } from 'api/queries'
import { useLocalization } from 'localization'
import { useMutation, useQueryClient } from 'react-query'
import { ResponseErrorType } from 'types/custom'
import {
	addTransaction,
	approveNeedsReviewTransaction,
	combineTransfer,
	combineTransfersAuto,
	csvTransactionImport,
	editTransaction,
	removeAllTransaction,
	removeTransaction,
} from './services'

export const useAddTransaction = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()
	const getErrorMessage = useGetErrorMessage()

	return useMutation(addTransaction, {
		onSuccess: () => {
			message.success(t('transactions.add.success'))
			queryClient.invalidateQueries([queries.transactions])
			queryClient.invalidateQueries([queries.transactionStatistic])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(
					getErrorMessage({
						response: error.response,
						defaultKey: 'transactions.add.error',
					})
				)
			}
		},
	})
}

export const useEditTransaction = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()
	const getErrorMessage = useGetErrorMessage()

	return useMutation(editTransaction, {
		onSuccess: (_, { id }) => {
			message.success(t('transactions.edit.success'))
			queryClient.invalidateQueries([queries.transactions])
			queryClient.invalidateQueries([queries.transactionById, id])
			queryClient.invalidateQueries([queries.transactionStatistic])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(
					getErrorMessage({
						response: error.response,
						defaultKey: 'transactions.edit.error',
					})
				)
			}
		},
	})
}

export const useApproveNeedsReviewTransaction = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(approveNeedsReviewTransaction, {
		onSuccess: (_, id) => {
			message.success(t('transactions.approve.success'))
			queryClient.invalidateQueries([queries.transactions])
			queryClient.invalidateQueries([queries.transactionById, id])
			queryClient.invalidateQueries([queries.transactionStatistic])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.approve.error'))
			}
		},
	})
}

export const useRemoveTransaction = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeTransaction, {
		onSuccess: (_, id) => {
			message.success(t('transactions.remove.success'))
			queryClient.invalidateQueries([queries.transactions])
			queryClient.invalidateQueries([queries.transactionById, id])
			queryClient.invalidateQueries([queries.transactionStatistic])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.remove.error'))
			}
		},
	})
}

export const useRemoveAllTransaction = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(removeAllTransaction, {
		onSuccess: () => {
			message.success(t('transactions.removeAll.success'))
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.removeAll.error'))
			}
		},
	})
}

export const useTransactionCSVImport = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(csvTransactionImport, {
		onSuccess: () => {
			message.success(t('transactions.csvUploadModal.success'))
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.csvUploadModal.error'))
			}
		},
	})
}

export const useCombineTransfer = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(combineTransfer, {
		onSuccess: () => {
			message.success(t('transactions.createTransferModal.success'))
			queryClient.invalidateQueries([queries.transactions])
			queryClient.invalidateQueries([queries.transactionById])
			queryClient.invalidateQueries([queries.transactionStatistic])
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.createTransferModal.error'))
			}
		},
	})
}

export const useAutoCombineTransfers = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(combineTransfersAuto, {
		onSuccess: () => {
			message.success(t('transactions.transfersAutoCombine.success'))
			queryClient.invalidateQueries([queries.featureAccess])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('transactions.transfersAutoCombine.error'))
			}
		},
	})
}
